import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, X, Download, ExternalLink } from 'lucide-react';

interface PropertyFloorplanProps {
  planimetriaUrl?: string | null;
  propertyName: string;
  isOpen: boolean;
  onClose: () => void;
}

const PropertyFloorplan: React.FC<PropertyFloorplanProps> = ({ planimetriaUrl, propertyName, isOpen, onClose }) => {
  if (!planimetriaUrl) return null;

  const isPDF = planimetriaUrl.toLowerCase().endsWith('.pdf');
  
  const handleDownload = () => {
    // Creare un elemento a invisibile per triggare il download
    const link = document.createElement('a');
    link.href = planimetriaUrl;
    link.download = `Planimetria_${propertyName.replace(/\s+/g, '_')}.${isPDF ? 'pdf' : 'jpg'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleOpenInNewTab = () => {
    window.open(planimetriaUrl, '_blank');
  };

  return (
    <AnimatePresence>
      {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95 backdrop-blur-sm p-4"
            onClick={onClose}
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative max-w-5xl w-full max-h-[90vh] p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-4 right-4 flex gap-3 z-20">
                <button
                  onClick={handleOpenInNewTab}
                  className="p-2 bg-primary hover:bg-primary-light rounded-full text-white transition-colors shadow-lg"
                  title="Apri in nuova scheda"
                >
                  <ExternalLink className="h-6 w-6" />
                </button>
                <button
                  onClick={handleDownload}
                  className="p-2 bg-secondary hover:bg-secondary-light rounded-full text-white transition-colors shadow-lg"
                  title="Scarica planimetria"
                >
                  <Download className="h-6 w-6" />
                </button>
                <button 
                  onClick={onClose} 
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors shadow-lg"
                  title="Chiudi"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {isPDF ? (
                <iframe
                  src={`${planimetriaUrl}#toolbar=1&navpanes=0`}
                  className="w-full h-[85vh] bg-white rounded-lg shadow-2xl"
                  title="Planimetria immobile"
                />
              ) : (
                <img
                  src={planimetriaUrl}
                  alt={`Planimetria di ${propertyName}`}
                  className="max-w-full max-h-[85vh] mx-auto object-contain bg-white rounded-lg shadow-2xl"
                />
              )}
            </motion.div>
          </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PropertyFloorplan;